import React, { useState, useEffect, useCallback } from "react";
import { Async } from "react-select";
import { Switch, Text, Button, Modal, Input, Loading } from "@nextui-org/react";
import { useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";
import { Calendar } from "react-iconly";

import endpoint from "../../config/endpoint.json";
import ListItems from "../../components/PageSelectionComponent/ListItems";
import SubPageHeader from "../../components/SubPageHeader";
import { secureFetch } from "../../lib/api";
import { searchOrderList } from "../../lib/utils";
import { error, notify } from "../../lib/userAlerts";
import { mstore } from "../../store";
import checkAccess from '../../lib/checkAccess';

const Osd = ({ location }) => {
  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const Access = useSelector(state => state.navigation.Access);
  const dispatch = useDispatch();

  const [state, setState] = useState({
    orderNo: "",
    exp1: "",
    exp2: "",
    OS: 1,
    stopId: "",
    stopName: "",
    driverId: "",
    bolNumber: "",
    withReview: 0,
    noOfFilter: 0,
    clicked: false,
    orderList: [],
    artifactsList: [],
    activeArtifactsList: [],
    bolList: [],
    orderNoList: [],
    loading: false,
    selectedPage: 1,
    count: 7,
    totalPages: 0,
    trimmedOrderList: [],
    allowRatingsArtifax: null,
    coordinatorId: "",
    openPopover: false,
    openPopoverUpload: false,
    openPopoverDelete: false,
    csvShipmentList: [],
    shipmentFile: null,
    uploadedCount: 0,
    failedCount: 0,
    selectedOrders: {},
    previewDoc: "",
    pdfLoading: false,
    osdDocType: "",
    partnerDocument: false,
    isBroker: 0,
    orderWithDocuments: false,
    openDateSearchModal: false
  });

  // Fetch company details
  const fetchCompanyDetails = useCallback(async () => {
    try {
      const [ok, status, company] = await secureFetch(endpoint.getCompanyDetails);
      if (ok) {
        setState(prev => ({ ...prev, isBroker: company.is_broker }));
      } else {
        error("Oops! error occurred during fetch company details");
      }
    } catch (err) {
      error("Oops! error occurred during fetch company details");
    }
  }, []);

  // Get artifacts list
  const getArtifactsList = useCallback(async () => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.getArtifactsList, {});
      if (ok) {
        const activeArtifactsList = json.artifactsList
          .filter(item => item.status !== 0 && item.is_osd === 1);
        
        setState(prev => ({
          ...prev,
          artifactsList: activeArtifactsList,
          activeArtifactsList: activeArtifactsList,
          osdDocType: activeArtifactsList[0]?.type_name || ''
        }));
      } else {
        error("Oops! error occurred during fetching artifacts list!");
      }
    } catch (err) {
      error("Oops! error occurred during fetching artifacts list!");
    }
  }, []);

  // Handle order number search
  const getOrderNO = useCallback(async (input, callback) => {
    let orderNumbers = [];

    if (state.orderNoList?.length > 0) {
      orderNumbers = searchOrderList(state.orderNoList, input);
    }

    if (orderNumbers?.length > 0) {
      callback(null, { options: orderNumbers, complete: true });
      return;
    }

    try {
      const [ok, status, json] = await secureFetch(endpoint.getOrderNO, {
        filter: input,
        os: state.OS,
        osFilterRequired: true,
      });

      if (ok) {
        const newOrderNumbers = json.orderNo.map(k => ({
          label: k.order_id,
          value: k.order_id,
        }));

        setState(prev => ({ ...prev, orderNoList: newOrderNumbers }));
        callback(null, { options: newOrderNumbers, complete: true });
      } else {
        error("Oops! Unable to fetch order number list.");
        callback(null, { options: [], complete: true });
      }
    } catch (err) {
      error("Oops! Unable to fetch order number list.");
      callback(null, { options: [], complete: true });
    }
  }, [state.OS, state.orderNoList]);

  // Handle BOL list search
  const getBolList = useCallback(async (input, callback) => {
    let bolNumbers = [];

    if (state.bolList?.length > 0) {
      bolNumbers = searchOrderList(state.bolList, input);
    }

    if (bolNumbers?.length > 0) {
      callback(null, { options: bolNumbers, complete: true });
      return;
    }

    try {
      const [ok, status, json] = await secureFetch(endpoint.getBolList, {
        filter: input,
        os: state.OS,
        osFilterRequired: true,
      });

      if (ok) {
        const newBolNumbers = json.bolList.map(k => ({
          label: k.bol_number,
          value: k.bol_number
        }));

        setState(prev => ({ ...prev, bolList: newBolNumbers }));
        callback(null, { options: newBolNumbers, complete: true });
      } else {
        error("Oops! Unable to fetch BOL number list.");
        callback(null, { options: [], complete: true });
      }
    } catch (err) {
      error("Oops! Unable to fetch BOL number list.");
      callback(null, { options: [], complete: true });
    }
  }, [state.OS, state.bolList]);

  // Handle search
  const handleChange = useCallback(async (orderId, bolNumber) => {
    if (!bolNumber && !orderId && !state.exp1 && !state.exp2) {
      setState(prev => ({
        ...prev,
        trimmedOrderList: [],
        orderNo: "",
        bolNumber: "",
        previewDoc: "",
      }));
      notify("Please select any filter criteria");
      return;
    }

    if ((state.exp1 && !state.exp2) || (!state.exp1 && state.exp2)) {
      error("Please select start and end date both");
      return;
    }

    setState(prev => ({
      ...prev,
      orderNo: orderId,
      bolNumber: bolNumber,
      clicked: true,
      loading: true,
      csvShipmentList: [],
      previewDoc: "",
    }));

    try {
      const [ok, status, json] = await secureFetch(endpoint.searchShipmentListForCarrier, {
        exp1: state.exp1,
        exp2: state.exp2,
        bol_Number: bolNumber,
        orderNo: orderId,
        onlyCarrier: state.partnerDocument,
        onlyHaveDocumnet: state.orderWithDocuments,
        osd: true,
      });

      if (ok) {
        const totalPages = Math.ceil(json.orderList.length / 10);
        setState(prev => ({
          ...prev,
          selectedPage: 1,
          orderList: json.orderList,
          clicked: false,
          loading: false,
          openDateSearchModal: false,
          trimmedOrderList: json.orderList,
          totalPages,
          count: totalPages > 10 ? 10 : totalPages,
        }));
      } else {
        setState(prev => ({ ...prev, loading: false, openDateSearchModal: false }));
        error("Oops! error occurred during search order list!");
      }
    } catch (err) {
      setState(prev => ({ ...prev, loading: false, openDateSearchModal: false }));
      error("Oops! error occurred during search order list!");
    }
  }, [state.exp1, state.exp2, state.partnerDocument, state.orderWithDocuments]);

  // Set preview document
  const setPreview = useCallback(async (url) => {
    setState(prev => ({ ...prev, pdfLoading: true }));

    try {
      const response = await fetch(url, { method: "GET" });
      const blob = await response.blob();
      const file = new Blob([blob], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      setState(prev => ({ ...prev, previewDoc: fileURL }));
    } catch (err) {
      console.error(err);
      setState(prev => ({ ...prev, pdfLoading: false }));
    }
  }, []);

  // Initialize component
  useEffect(() => {
    checkAccess(clientId);
    getArtifactsList();
    fetchCompanyDetails();

    if (location?.state) {
      setState(prev => ({ ...prev, partnerDocument: true }));
      handleChange(null, location.state.order.bol_number);
    }
  }, [clientId, location, getArtifactsList, fetchCompanyDetails, handleChange]);

  // Handle client ID changes
  useEffect(() => {
    if (clientId) {
      checkAccess(clientId);
      getArtifactsList();
      fetchCompanyDetails();
      setState(prev => ({
        ...prev,
        orderNoList: [],
        bolList: []
      }));
    }
  }, [clientId, getArtifactsList, fetchCompanyDetails]);

  // Render order list
  const renderOrderList = () => {
    if (!state.loading && !state.orderNo && !state.exp1 && !state.exp2 && state.trimmedOrderList.length === 0) {
      return <div className="ml2 mt3 f4 tc">Please select any filter criteria</div>;
    }

    if (!state.loading && state.trimmedOrderList.length === 0) {
      return <div className="ml2 mt3 f4 tc">Order not available</div>;
    }

    return (
      <div id="scrollTop" style={{ height: window.innerHeight }}>
        {state.loading ? (
          <div className="flex justify-center mt6">
            <Loading size="lg">Fetching OS&D Data. Hang on..</Loading>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {state.trimmedOrderList.map((item, index) => (
              <div 
                key={index} 
                className={`
                  bg-white rounded-lg shadow-sm border border-gray-100
                  transition-all duration-200 ease-in-out
                  hover:shadow-md hover:border-gray-200
                  ${state.selectedOrders[index] ? "bg-red-50" : ""}
                `}
              >
                <ListItems
                  allowRatingsArtifax={state.allowRatingsArtifax}
                  itemIndex={index}
                  documents={item.document || {}}
                  os={item.os}
                  ordderNo={item.OrderNo}
                  stop={item.Stop}
                  StopType={item.StopType}
                  StopName={item.StopName}
                  address={item.Address}
                  bolNumber={item.bol_number}
                  shipmentTime={item.StopArriveTime}
                  shipmentId={item.StopId}
                  orderNoList={state.orderNoList}
                  artifactsList={state.artifactsList}
                  activeArtifactsList={state.activeArtifactsList}
                  searchOrderNo={state.orderNo}
                  searchBolNumber={state.bolNumber}
                  getShipmentList={handleChange}
                  setPreview={setPreview}
                  osdDocType={state.osdDocType}
                  checkOs={state.OS}
                  autoCloseInd={item.auto_close_ind}
                  autoCloseDate={item.auto_close_date}
                  driverCloseTs={item.driver_close_ts}
                  sdnRecordId={item.s_rec}
                  driver1EmpId={item.Driver1}
                  driver2EmpId={item.Driver2}
                  driver1Name={item.Driver1Name}
                  driver2Name={item.Driver2Name}
                  driver1Email={item.Driver1email}
                  driver2Email={item.Driver2email}
                  driver1Phone={item.Driver1phone}
                  driver2Phone={item.Driver2phone}
                  driver1UserId={item.driver1UserId}
                  driver2UserId={item.driver2UserId}
                  clientId={item.transporter_client_id || item.client_id}
                  recordSource={item.comapnyName || ""}
                  comments={item.Comment}
                  pushNotificationCount={item.pushNotificationCount}
                  isBroker={state.isBroker}
                  allowBrokersToReopenClosedStop={item.allow_brokers_to_reopen_closed_stop}
                  allowBrokersToSendMessage={item.allow_brokers_to_send_message}
                  allowBrokerPushNotification={state.isBroker ? Access.allow_broker_push_notification : 1}
                  partnerDocument={item.transporter_client_id ? true : false}
                  allowSendMessage={item.allow_send_message}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render filter section
  const renderFilterSection = () => (
    <div className="flex" key={clientId}>
      <div className="w-100 flex items-center flex-wrap">
        <Async
          name="Search order"
          style={{ width: "200px" }}
          placeholder="Search order"
          value={state.orderNo}
          onChange={(item) => handleChange(item?.value || "", state.bolNumber)}
          options={state.orderNoList}
          loadOptions={debounce(getOrderNO, 500)}
        />
        <Async
          name="Search by BOL"
          className="px-4"
          style={{ width: "200px" }}
          placeholder="Search by BOL"
          value={state.bolNumber}
          onChange={(item) => handleChange(state.orderNo, item?.value || "")}
          options={state.bolList}
          loadOptions={debounce(getBolList, 500)}
        />
        <Calendar
          primaryColor="blue"
          size={28}
          onClick={() => setState(prev => ({ ...prev, openDateSearchModal: true }))}
        />
        {state.isBroker === 1 && Access.allow_broker_carrier_data_docs === 1 && (
          <div className="flex justify-center items-center px-4">
            <Switch
              size="sm"
              checked={state.partnerDocument}
              onChange={() => {
                setState(prev => ({
                  ...prev,
                  trimmedOrderList: [],
                  orderNo: "",
                  bolNumber: "",
                  partnerDocument: !prev.partnerDocument,
                }));
              }}
            />
            <Text size="1rem" p className="px-2">
              Partner Document
            </Text>
          </div>
        )}
        <div className="flex justify-center items-center">
          <Switch
            size="sm"
            checked={state.orderWithDocuments}
            onChange={() => {
              setState(prev => ({
                ...prev,
                trimmedOrderList: [],
                orderNo: "",
                bolNumber: "",
                orderWithDocuments: !prev.orderWithDocuments,
              }));
            }}
          />
          <Text size="1rem" p className="px-2">
            Only include stop with documents
          </Text>
        </div>
      </div>
    </div>
  );

  // Render date search modal
  const renderDateSearchModal = () => (
    <Modal
      open={state.openDateSearchModal}
      onClose={() => setState(prev => ({ ...prev, openDateSearchModal: false, exp1: '', exp2: '' }))}
      closeButton
      blur
      css={{ br: 0 }}
    >
      <Modal.Header>
        <Text size={18}>Search Date Range</Text>
      </Modal.Header>
      <Modal.Body>
        <div className="flex">
          <Input
            underlined
            clearable
            css={{ w: '100%' }}
            type="date"
            label="From Date"
            onChange={(e) => setState(prev => ({ ...prev, exp1: e.target.value }))}
            value={state.exp1}
          />
          <Input
            css={{ ml: 10, w: '100%' }}
            underlined
            clearable
            label="To Date"
            type="date"
            onChange={(e) => setState(prev => ({ ...prev, exp2: e.target.value }))}
            value={state.exp2}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          auto
          css={{ br: 0 }}
          onClick={() => handleChange(state.orderNo, state.bolNumber)}
        >
          Search
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (state.allowRatingsArtifax === 0) {
    return (
      <div>
        <div className="ml3 w-100">
          <h4 className="f3 fw4 mb1 pt1 pb3 ph1 mt2">OS&D Stops</h4>
        </div>
        <div className="ml2 mt3 f5">This feature is unavailable</div>
      </div>
    );
  }

  return (
    <div>
      <div className="p-4 flex border-y-2 border-dotted justify-between">
        <SubPageHeader>OS&D Stops</SubPageHeader>
        {renderFilterSection()}
      </div>
      <div className="flex">
        <div className="w-50 overflow-y-auto vh-till-bottom">
          {renderOrderList()}
        </div>
        <div className="w-50 flex flex-column items-center mt1 mr2 bg-near-white">
          <iframe
            src={state.previewDoc}
            className="vh-till-bottom-doc"
            onLoad={() => setState(prev => ({ ...prev, pdfLoading: false }))}
            width="100%"
            style={{ border: 0 }}
            frameBorder="0"
          />
          {state.pdfLoading && (
            <div className="flex justify-center items-center w-full h-full absolute inset-0 bg-white bg-opacity-75">
              <Loading size="lg" color="primary">Loading document...</Loading>
            </div>
          )}
        </div>
      </div>
      {renderDateSearchModal()}
    </div>
  );
};

export default Osd;